import { ReactNode, useCallback, useEffect, useState } from 'react';
import { sample } from 'lodash';
import { Box, Modal } from '@mui/material';
import { useRecoilState } from 'recoil';
import { Link } from 'react-router-dom';
import { usePopups } from '../../hooks/usePopups';
import { Popup } from '../../interfaces/Popup/Popup';
import { useDebounce } from '../../hooks/useDebounce';
import { PopupController, PopupHost } from './PopupController';
import {
  getStoredPopupsShown,
  incrementPopupShowCount,
} from '../../utils/Popup';
import { sessionPopupsAtom } from '../../atoms/SessionPopups';

export const PopupProvider = ({ children }: { children?: ReactNode }) => {
  const popups = usePopups();

  const [sessionPopups, setSessionPopups] = useRecoilState(sessionPopupsAtom);

  const [popup, setPopup] = useState<Popup | undefined>(undefined);

  const debouncedPopup = useDebounce(popup, (popup?.delaySeconds ?? 0) * 1000);

  useEffect(() => {
    if (debouncedPopup) {
      incrementPopupShowCount(debouncedPopup.id);
      setSessionPopups((prev) => [...prev, debouncedPopup.id]);
    }
  }, [debouncedPopup]);

  const initiate = useCallback(
    (host: PopupHost) => {
      const popupsShown = getStoredPopupsShown();

      let potentialPopups = popups.filter(
        (x) =>
          (!x.maxShowPerUser || x.maxShowPerUser > (popupsShown[x.id] ?? 0)) &&
          !sessionPopups.includes(x.id),
      );

      if (host.isHomePage) {
        potentialPopups = potentialPopups.filter((x) => x.showInHome);
      } else if (host.productSlug) {
        potentialPopups = potentialPopups.filter(
          (x) =>
            x.showInAllProducts ||
            x.relations.productSlugs.includes(host.productSlug!),
        );
      } else if (host.categorySlug) {
        potentialPopups = potentialPopups.filter(
          (x) =>
            x.showInAllCategories ||
            x.relations.categorySlugs.includes(host.categorySlug!),
        );
      } else if (host.brandSlug) {
        potentialPopups = potentialPopups.filter(
          (x) =>
            x.showInAllBrands ||
            x.relations.brandSlugs.includes(host.brandSlug!),
        );
      } else {
        potentialPopups = [];
      }

      const selectedPopup = sample(potentialPopups);

      if (!potentialPopups.length || !selectedPopup) {
        setPopup(undefined);

        return;
      }

      setPopup(selectedPopup);
    },
    [popups, sessionPopups],
  );

  const cancel = useCallback(() => setPopup(undefined), []);

  const content = (
    <Box
      component='img'
      width='90vw'
      onClick={cancel}
      height='auto'
      maxWidth='600px'
      src={debouncedPopup?.image}
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      }}
    />
  );

  return (
    <PopupController initiate={initiate} cancel={cancel}>
      <Modal open={!!debouncedPopup} onClose={cancel}>
        {debouncedPopup?.linkUrl ? (
          <Link to={debouncedPopup.linkUrl}>{content}</Link>
        ) : (
          content
        )}
      </Modal>

      {children}
    </PopupController>
  );
};
